import {
  Box,
  Card,
  Text,
  Table,
  Badge,
  Button,
  Group,
  ActionIcon,
  Stack,
  Center,
  Pagination,
  LoadingOverlay,
} from '@mantine/core';
import { IconDownload, IconEye, IconReceipt, IconFileSpreadsheet } from '@tabler/icons-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getBillingHistory,
  getBillingStatistics,
  exportBillingHistory,
  BillingHistoryItem,
  BillingStatus,
  BillingStatistics,
} from '@mygpt/app';
import { showNotification } from '@mantine/notifications';

export const BillingHistory = () => {
  const { t, i18n } = useTranslation();
  const [billingRecords, setBillingRecords] = useState<BillingHistoryItem[]>([]);
  const [statistics, setStatistics] = useState<BillingStatistics | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 20;

  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        const res = await getBillingStatistics();
        if (res.data) {
          setStatistics(res.data);
        }
      } catch (error: any) {
        // 统计数据失败不影响主要功能
      }
    };

    fetchStatistics();
  }, []);

  useEffect(() => {
    const fetchBillingHistory = async () => {
      setLoading(true);
      try {
        const res = await getBillingHistory({ page: currentPage, page_size: pageSize });
        if (res.data) {
          setBillingRecords(res.data.items || []);
          setTotal(res.data.total || 0);
          setTotalPages(res.data.total_pages || 1);
        }
      } catch (error: any) {
        showNotification({
          color: 'red',
          title: t('billing.history.messages.loadFailed'),
          message: error.message || t('billing.history.messages.loadFailed'),
        });
      } finally {
        setLoading(false);
      }
    };

    fetchBillingHistory();
  }, [currentPage]);

  const handleExportBilling = async () => {
    setExporting(true);
    try {
      const langMap: Record<string, 'ja' | 'zh' | 'en'> = {
        ja: 'ja',
        'zh-Hans': 'zh',
        en: 'en',
      };
      const exportLang = langMap[i18n.language] || 'ja';
      await exportBillingHistory({ lang: exportLang });

      showNotification({
        color: 'green',
        title: t('billing.history.messages.exportSuccess'),
        message: t('billing.history.messages.exportSuccess'),
      });
    } catch (error: any) {
      showNotification({
        color: 'red',
        title: t('billing.history.messages.exportFailed'),
        message: error.message || t('billing.history.messages.exportFailed'),
      });
    } finally {
      setExporting(false);
    }
  };

  const getStatusBadge = (status: BillingStatus) => {
    const statusConfig = {
      [BillingStatus.SUCCEEDED]: { color: 'green', label: t('billing.history.status.succeeded') },
      [BillingStatus.PENDING]: { color: 'yellow', label: t('billing.history.status.pending') },
      [BillingStatus.FAILED]: { color: 'red', label: t('billing.history.status.failed') },
      [BillingStatus.REFUNDED]: { color: 'gray', label: t('billing.history.status.refunded') },
      [BillingStatus.PARTIALLY_REFUNDED]: { color: 'orange', label: t('billing.history.status.partiallyRefunded') },
    };

    const config = statusConfig[status] || statusConfig[BillingStatus.SUCCEEDED];

    return (
      <Badge color={config.color} variant="light" size="sm">
        {config.label}
      </Badge>
    );
  };

  const handleDownloadInvoice = (record: BillingHistoryItem) => {
    if (record.invoice_pdf_url) {
      window.open(record.invoice_pdf_url, '_blank');
    } else {
      showNotification({
        color: 'orange',
        message: t('billing.history.messages.noPdf'),
      });
    }
  };

  const handleViewInvoice = (record: BillingHistoryItem) => {
    if (record.invoice_url) {
      window.open(record.invoice_url, '_blank');
    } else {
      showNotification({
        color: 'orange',
        message: t('billing.history.messages.noInvoice'),
      });
    }
  };

  return (
    <Box>
      <Group justify="space-between" mb="lg">
        <div>
          <Text size="lg" fw={600}>
            {t('billing.history.title')}
          </Text>
          {total > 0 && (
            <Text size="sm" c="dimmed" mt={4}>
              {t('billing.history.totalCount', { total })}
            </Text>
          )}
        </div>
        <Button
          variant="light"
          size="sm"
          leftSection={<IconFileSpreadsheet size={16} />}
          onClick={handleExportBilling}
          loading={exporting}
          disabled={total === 0}
        >
          {t('billing.history.export')}
        </Button>
      </Group>

      <Card shadow="sm" padding="0" radius="md" withBorder pos="relative">
        <LoadingOverlay visible={loading} />
        {!loading && billingRecords.length === 0 ? (
          <Center py="xl">
            <Stack align="center" gap="md">
              <IconReceipt size={48} color="var(--mantine-color-gray-4)" />
              <Text c="dimmed">{t('billing.history.empty')}</Text>
            </Stack>
          </Center>
        ) : (
          <>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('billing.history.table.date')}</Table.Th>
                  <Table.Th>{t('billing.history.table.plan')}</Table.Th>
                  <Table.Th>{t('billing.history.table.amount')}</Table.Th>
                  <Table.Th>{t('billing.history.table.status')}</Table.Th>
                  <Table.Th>{t('billing.history.table.invoice')}</Table.Th>
                  <Table.Th>{t('billing.history.table.actions')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {billingRecords.map((record) => (
                  <Table.Tr key={record.id}>
                    <Table.Td>
                      <Text size="sm">{record.billing_date}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {record.plan_name}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={600}>
                        {record.amount_display}
                      </Text>
                    </Table.Td>
                    <Table.Td>{getStatusBadge(record.status)}</Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {record.invoice_number || '-'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          onClick={() => handleViewInvoice(record)}
                          title={t('billing.history.table.viewTooltip')}
                          disabled={!record.invoice_url}
                        >
                          <IconEye size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          onClick={() => handleDownloadInvoice(record)}
                          title={t('billing.history.table.downloadTooltip')}
                          disabled={!record.invoice_pdf_url}
                        >
                          <IconDownload size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>

            {totalPages > 1 && (
              <Group justify="center" py="md">
                <Pagination value={currentPage} onChange={setCurrentPage} total={totalPages} size="sm" />
              </Group>
            )}
          </>
        )}
      </Card>

      <Card shadow="sm" padding="lg" radius="md" withBorder mt="lg">
        <Group justify="space-between" mb="md">
          <Text fw={600}>{t('billing.history.statistics.title')}</Text>
          {statistics && (
            <Text size="xs" c="dimmed">
              {statistics.currency.toUpperCase()}
            </Text>
          )}
        </Group>
        <Group justify="space-around">
          <Stack align="center" gap="xs">
            <Text size="xl" fw={700} c="blue">
              {statistics?.total_spent_display || '¥0'}
            </Text>
            <Text size="sm" c="dimmed">
              {t('billing.history.statistics.totalSpent')}
            </Text>
          </Stack>
          <Stack align="center" gap="xs">
            <Text size="xl" fw={700} c="green">
              {statistics?.total_invoices || 0}
            </Text>
            <Text size="sm" c="dimmed">
              {t('billing.history.statistics.totalInvoices')}
            </Text>
          </Stack>
          <Stack align="center" gap="xs">
            <Text size="xl" fw={700} c="orange">
              {statistics?.monthly_average_display || '¥0'}
            </Text>
            <Text size="sm" c="dimmed">
              {t('billing.history.statistics.monthlyAverage')}
            </Text>
          </Stack>
        </Group>
      </Card>
    </Box>
  );
};
